import React, { useState, useEffect } from 'react';
import { 
  Users, 
  MessageCircle, 
  Heart, 
  Share2, 
  Search, 
  Filter, 
  MapPin, 
  Clock, 
  TrendingUp,
  UserPlus,
  Eye,
  ChevronDown,
  Bell,
  Settings,
  Globe,
  Lock,
  Star,
  Camera,
  Mic,
  MoreHorizontal,
  ThumbsUp,
  Bookmark,
  Send
} from 'lucide-react';

const SocialHub = ({ darkMode = false }) => {
  const [activeTab, setActiveTab] = useState('discover');
  const [selectedPost, setSelectedPost] = useState(null);
  const [newPostContent, setNewPostContent] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showComments, setShowComments] = useState({});
  const [likedPosts, setLikedPosts] = useState(new Set());

  // Mock data for demonstration
  const [posts, setPosts] = useState([
    {
      id: 1,
      author: 'Alex Rivera',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      time: '2h ago',
      location: 'San Francisco, CA',
      content: 'Just discovered this amazing coffee shop that doubles as a coworking space! The community here is incredible - met three potential collaborators for my startup idea. Sometimes the best connections happen in the most unexpected places ☕',
      image: 'https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?w=500&h=300&fit=crop',
      likes: 127,
      comments: 23,
      shares: 8,
      tags: ['startup', 'networking', 'coffee'],
      verified: true
    },
    {
      id: 2,
      author: 'Maria Santos',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      time: '4h ago',
      location: 'Austin, TX',
      content: 'Hosting a sustainable living workshop this weekend! We\'ll cover urban gardening, zero-waste cooking, and DIY natural products. Perfect for anyone looking to reduce their environmental footprint while building community connections 🌱',
      likes: 89,
      comments: 31,
      shares: 15,
      tags: ['sustainability', 'workshop', 'community'],
      verified: false
    },
    {
      id: 3,
      author: 'David Chen',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      time: '6h ago',
      location: 'New York, NY',
      content: 'The future of remote work is here! Just finished an amazing VR collaboration session with my team across 4 different countries. The technology is getting so immersive that it felt like we were in the same room.',
      image: 'https://images.unsplash.com/photo-1593508512255-86ab42a8e620?w=500&h=300&fit=crop',
      likes: 203,
      comments: 45,
      shares: 22,
      tags: ['remote-work', 'VR', 'technology'],
      verified: true
    }
  ]);

  const [suggestedPeople, setSuggestedPeople] = useState([
    {
      id: 1,
      name: 'Sarah Johnson',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
      title: 'UI/UX Designer',
      mutualConnections: 12,
      interests: ['Design', 'Tech', 'Travel'],
      compatibility: 94
    },
    {
      id: 2,
      name: 'Michael Torres',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face',
      title: 'Full Stack Developer',
      mutualConnections: 8,
      interests: ['Coding', 'Music', 'Gaming'],
      compatibility: 87
    },
    {
      id: 3,
      name: 'Emily Wang',
      avatar: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=150&h=150&fit=crop&crop=face',
      title: 'Data Scientist',
      mutualConnections: 15,
      interests: ['AI', 'Research', 'Books'],
      compatibility: 91
    }
  ]);

  const handleLike = (postId) => {
    const newLikedPosts = new Set(likedPosts);
    if (newLikedPosts.has(postId)) {
      newLikedPosts.delete(postId);
    } else {
      newLikedPosts.add(postId);
    }
    setLikedPosts(newLikedPosts);
    
    setPosts(posts.map(post => 
      post.id === postId 
        ? { ...post, likes: likedPosts.has(postId) ? post.likes - 1 : post.likes + 1 }
        : post
    ));
  };

  const toggleComments = (postId) => {
    setShowComments(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  const bgClass = darkMode 
    ? 'bg-gradient-to-br from-slate-900 via-blue-900 to-emerald-900/20'
    : 'bg-gradient-to-br from-blue-50 via-emerald-50 to-white';

  return (
    <div className={`min-h-screen ${bgClass} transition-all duration-300`}>
      {/* Header */}
      <div className={`sticky top-0 z-50 backdrop-blur-md border-b ${
        darkMode 
          ? 'bg-slate-900/80 border-slate-700' 
          : 'bg-white/80 border-gray-200'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-emerald-500 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <span className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  SocialHub
                </span>
              </div>
              
              <div className="hidden md:flex space-x-1">
                {['discover', 'people', 'events'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 rounded-lg font-medium capitalize transition-all ${
                      activeTab === tab
                        ? darkMode 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-blue-500 text-white'
                        : darkMode
                          ? 'text-gray-300 hover:text-white hover:bg-slate-800'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`} />
                <input
                  type="text"
                  placeholder="Search people, posts, events..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`pl-10 pr-4 py-2 w-64 rounded-lg border ${
                    darkMode 
                      ? 'bg-slate-800 border-slate-700 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
              </div>
              
              <button className={`p-2 rounded-lg ${
                darkMode ? 'text-gray-400 hover:text-white hover:bg-slate-800' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}>
                <Bell className="w-5 h-5" />
              </button>
              
              <button className={`p-2 rounded-lg ${
                darkMode ? 'text-gray-400 hover:text-white hover:bg-slate-800' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}>
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Left Sidebar - People Suggestions */}
          <div className="lg:col-span-1">
            <div className={`rounded-2xl p-6 ${
              darkMode ? 'bg-slate-800/50 backdrop-blur-sm' : 'bg-white/70 backdrop-blur-sm'
            } shadow-xl border ${darkMode ? 'border-slate-700' : 'border-white/20'}`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  People Like You
                </h2>
                <Filter className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
              </div>
              
              <div className="space-y-4">
                {suggestedPeople.map((person) => (
                  <div key={person.id} className={`p-4 rounded-xl border ${
                    darkMode ? 'bg-slate-900/50 border-slate-600' : 'bg-white/50 border-gray-200'
                  } hover:scale-105 transition-transform`}>
                    <div className="flex items-center space-x-3 mb-3">
                      <img
                        src={person.avatar}
                        alt={person.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div className="flex-1">
                        <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {person.name}
                        </h3>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {person.title}
                        </p>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className={`w-full bg-gray-200 rounded-full h-2 ${darkMode ? 'bg-gray-700' : ''}`}>
                          <div 
                            className="bg-gradient-to-r from-blue-500 to-emerald-500 h-2 rounded-full"
                            style={{ width: `${person.compatibility}%` }}
                          ></div>
                        </div>
                        <span className={`text-sm font-medium ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                          {person.compatibility}%
                        </span>
                      </div>
                      <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                        {person.mutualConnections} mutual connections
                      </p>
                    </div>
                    
                    <div className="flex flex-wrap gap-1 mb-3">
                      {person.interests.map((interest, index) => (
                        <span
                          key={index}
                          className={`px-2 py-1 text-xs rounded-full ${
                            darkMode 
                              ? 'bg-blue-900/50 text-blue-300' 
                              : 'bg-blue-100 text-blue-700'
                          }`}
                        >
                          {interest}
                        </span>
                      ))}
                    </div>
                    
                    <button className="w-full py-2 px-4 bg-gradient-to-r from-blue-500 to-emerald-500 text-white rounded-lg font-medium hover:from-blue-600 hover:to-emerald-600 transition-all">
                      Connect
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Create Post */}
            <div className={`rounded-2xl p-6 mb-6 ${
              darkMode ? 'bg-slate-800/50 backdrop-blur-sm' : 'bg-white/70 backdrop-blur-sm'
            } shadow-xl border ${darkMode ? 'border-slate-700' : 'border-white/20'}`}>
              <div className="flex items-center space-x-4 mb-4">
                <img
                  src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face"
                  alt="Your avatar"
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div className="flex-1">
                  <textarea
                    placeholder="What's on your mind? Share your thoughts with the community..."
                    value={newPostContent}
                    onChange={(e) => setNewPostContent(e.target.value)}
                    className={`w-full p-3 rounded-xl border resize-none ${
                      darkMode 
                        ? 'bg-slate-900/50 border-slate-600 text-white placeholder-gray-400' 
                        : 'bg-white/50 border-gray-200 text-gray-900 placeholder-gray-500'
                    } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    rows="3"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <button className={`flex items-center space-x-2 px-3 py-2 rounded-lg ${
                    darkMode ? 'text-gray-400 hover:text-white hover:bg-slate-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}>
                    <Camera className="w-5 h-5" />
                    <span className="text-sm">Photo</span>
                  </button>
                  <button className={`flex items-center space-x-2 px-3 py-2 rounded-lg ${
                    darkMode ? 'text-gray-400 hover:text-white hover:bg-slate-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}>
                    <Mic className="w-5 h-5" />
                    <span className="text-sm">Audio</span>
                  </button>
                  <button className={`flex items-center space-x-2 px-3 py-2 rounded-lg ${
                    darkMode ? 'text-gray-400 hover:text-white hover:bg-slate-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}>
                    <MapPin className="w-5 h-5" />
                    <span className="text-sm">Location</span>
                  </button>
                </div>
                
                <button className="px-6 py-2 bg-gradient-to-r from-blue-500 to-emerald-500 text-white rounded-lg font-medium hover:from-blue-600 hover:to-emerald-600 transition-all">
                  Share
                </button>
              </div>
            </div>

            {/* Posts Feed */}
            <div className="space-y-6">
              {posts.map((post) => (
                <div key={post.id} className={`rounded-2xl p-6 ${
                  darkMode ? 'bg-slate-800/50 backdrop-blur-sm' : 'bg-white/70 backdrop-blur-sm'
                } shadow-xl border ${darkMode ? 'border-slate-700' : 'border-white/20'} hover:shadow-2xl transition-all`}>
                  {/* Post Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <img
                        src={post.avatar}
                        alt={post.author}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {post.author}
                          </h3>
                          {post.verified && (
                            <Star className="w-4 h-4 text-blue-500 fill-current" />
                          )}
                        </div>
                        <div className="flex items-center space-x-2 text-sm">
                          <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                            {post.time}
                          </span>
                          <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>•</span>
                          <span className={`flex items-center space-x-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            <MapPin className="w-3 h-3" />
                            <span>{post.location}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <button className={`p-2 rounded-lg ${
                      darkMode ? 'text-gray-400 hover:text-white hover:bg-slate-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}>
                      <MoreHorizontal className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Post Content */}
                  <div className="mb-4">
                    <p className={`text-base leading-relaxed mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                      {post.content}
                    </p>
                    
                    {post.image && (
                      <img
                        src={post.image}
                        alt="Post content"
                        className="w-full h-64 object-cover rounded-xl"
                      />
                    )}
                    
                    {post.tags && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {post.tags.map((tag, index) => (
                          <span
                            key={index}
                            className={`text-sm px-3 py-1 rounded-full ${
                              darkMode 
                                ? 'bg-emerald-900/50 text-emerald-300' 
                                : 'bg-emerald-100 text-emerald-700'
                            }`}
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Post Actions */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center space-x-6">
                      <button
                        onClick={() => handleLike(post.id)}
                        className={`flex items-center space-x-2 transition-all ${
                          likedPosts.has(post.id)
                            ? 'text-red-500'
                            : darkMode ? 'text-gray-400 hover:text-red-400' : 'text-gray-600 hover:text-red-500'
                        }`}
                      >
                        <Heart className={`w-5 h-5 ${likedPosts.has(post.id) ? 'fill-current' : ''}`} />
                        <span className="text-sm font-medium">{post.likes}</span>
                      </button>
                      
                      <button
                        onClick={() => toggleComments(post.id)}
                        className={`flex items-center space-x-2 ${
                          darkMode ? 'text-gray-400 hover:text-blue-400' : 'text-gray-600 hover:text-blue-500'
                        } transition-all`}
                      >
                        <MessageCircle className="w-5 h-5" />
                        <span className="text-sm font-medium">{post.comments}</span>
                      </button>
                      
                      <button className={`flex items-center space-x-2 ${
                        darkMode ? 'text-gray-400 hover:text-emerald-400' : 'text-gray-600 hover:text-emerald-500'
                      } transition-all`}>
                        <Share2 className="w-5 h-5" />
                        <span className="text-sm font-medium">{post.shares}</span>
                      </button>
                    </div>
                    
                    <button className={`p-2 rounded-lg ${
                      darkMode ? 'text-gray-400 hover:text-yellow-400' : 'text-gray-600 hover:text-yellow-500'
                    } transition-all`}>
                      <Bookmark className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Comments Section */}
                  {showComments[post.id] && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center space-x-3 mb-4">
                        <img
                          src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face"
                          alt="Your avatar"
                          className="w-8 h-8 rounded-full object-cover"
                        />
                        <div className="flex-1 flex items-center space-x-2">
                          <input
                            type="text"
                            placeholder="Write a comment..."
                            className={`flex-1 p-2 rounded-lg border ${
                              darkMode 
                                ? 'bg-slate-900/50 border-slate-600 text-white placeholder-gray-400' 
                                : 'bg-white/50 border-gray-200 text-gray-900 placeholder-gray-500'
                            } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                          />
                          <button className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
                            <Send className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right Sidebar - Trending & Events */}
          <div className="lg:col-span-1">
            <div className="space-y-6">
              {/* Trending Topics */}
              <div className={`rounded-2xl p-6 ${
                darkMode ? 'bg-slate-800/50 backdrop-blur-sm' : 'bg-white/70 backdrop-blur-sm'
              } shadow-xl border ${darkMode ? 'border-slate-700' : 'border-white/20'}`}>
                <div className="flex items-center space-x-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-orange-500" />
                  <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Trending Now
                  </h2>
                </div>
                
                <div className="space-y-3">
                  {[
                    { topic: 'Remote Work Revolution', posts: '2.1k posts' },
                    { topic: 'Sustainable Living', posts: '1.8k posts' },
                    { topic: 'AI Innovation', posts: '3.2k posts' },
                    { topic: 'Community Building', posts: '1.5k posts' }
                  ].map((trend, index) => (
                    <div key={index} className={`p-3 rounded-lg border ${
                      darkMode ? 'bg-slate-900/30 border-slate-600 hover:bg-slate-900/50' : 'bg-white/30 border-gray-200 hover:bg-white/50'
                    } cursor-pointer transition-all`}>
                      <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        #{trend.topic}
                      </h3>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {trend.posts}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Stats */}
              <div className={`rounded-2xl p-6 ${
                darkMode ? 'bg-slate-800/50 backdrop-blur-sm' : 'bg-white/70 backdrop-blur-sm'
              } shadow-xl border ${darkMode ? 'border-slate-700' : 'border-white/20'}`}>
                <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Your Impact
                </h2>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Profile Views
                    </span>
                    <span className={`font-semibold ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                      +23%
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      New Connections
                    </span>
                    <span className={`font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                      +12
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Post Engagement
                    </span>
                    <span className={`font-semibold ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                      +45%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialHub;